import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Select } from '@utilities';
import {
  applyServiceNameToUrl,
  isValidServiceName,
  readServiceNameFromUrl,
  reportInvalidServiceName,
} from '@helpers';
import { selectServiceContext, setActiveServiceName } from '@redux/slices/user';

import styles from './_index.module.scss';

/**
 * Inline service-model selector rendered above the Builder prompts. Hidden when
 * the user has fewer than 2 services (`resolveActiveServiceName` applies the
 * sole service automatically; Advanced Options shows it read-only).
 *
 * Changing the selection performs a full reload with the new
 * `?serviceName=<value>`, since the load-time path already re-fetches prompts
 * and Advanced Options under that param — a hard reload is the cheapest way
 * to flush every downstream cache without bespoke invalidation here.
 */
const ServiceSelector = () => {
  const { active, count, list } = useSelector(selectServiceContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // If the URL carries a `serviceName` the user can't use, the slice has
  // already fallen back; rewrite the URL so it matches the resolved service
  // and surface the mismatch via toast + telemetry.
  useEffect(() => {
    if (list.length === 0) return;
    const fromUrl = readServiceNameFromUrl();
    if (!fromUrl || isValidServiceName(fromUrl, list)) return;
    const url = applyServiceNameToUrl(new URL(window.location.href), active);
    navigate(`${url.pathname}${url.search}${url.hash}`, {
      preventScrollReset: true,
      replace: true,
    });
    reportInvalidServiceName({ allowed: list, fallback: active, fromUrl });
  }, [active, list, navigate]);

  const options = useMemo(() => list.map((name) => ({ label: name, value: name })), [list]);
  const value = useMemo(() => (active ? { label: active, value: active } : null), [active]);

  if (count < 2) return null;

  const handleChange = (option) => {
    const serviceName = option?.value ?? '';
    // Persist the explicit choice before the reload — the reducer writes
    // localStorage synchronously, and the full-page navigation below would
    // otherwise discard it (the post-reload bootstrap only reads storage).
    dispatch(setActiveServiceName(serviceName));
    const url = applyServiceNameToUrl(new URL(window.location.href), serviceName);
    window.location.assign(url.toString());
  };

  return (
    <div className={styles['service-selector']} data-testid="service-selector">
      <Select
        label="Service"
        onChange={handleChange}
        options={options}
        placeholder="Select a service"
        value={value}
      />
    </div>
  );
};

export default ServiceSelector;
